import React, { useEffect, useState } from "react";
import {
  Thermometer, Droplets, Gauge, Activity,
  Radio, Send, Copy, FileDigit, RefreshCw, Loader2, Lock,
} from "lucide-react";
import { toast } from "sonner";
import {
  readTelemetry,
  ingestTelemetry,
  handshakeExport,
  handshakeBroadcast,
  listReceipts,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function fmt(v, unit = "") {
  if (v === null || v === undefined) return "—";
  return `${v}${unit}`;
}
function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function BioDigital() {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [env, setEnv] = useState(null);
  const [loadingEnv, setLoadingEnv] = useState(true);

  const [temp, setTemp] = useState("");
  const [hum, setHum] = useState("");
  const [pres, setPres] = useState("");
  const [vel, setVel] = useState("");
  const [src, setSrc] = useState("manual");
  const [posting, setPosting] = useState(false);

  const [handshake, setHandshake] = useState(null);
  const [targetUrl, setTargetUrl] = useState("");
  const [targetName, setTargetName] = useState("peer-ai");
  const [bearer, setBearer] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const [receipts, setReceipts] = useState([]);
  const [lifetimeDocks, setLifetimeDocks] = useState(0);

  const loadEnv = async () => {
    setLoadingEnv(true);
    try { setEnv(await readTelemetry()); }
    catch (err) { console.warn("[BioDigital] telemetry read failed:", err); }
    finally { setLoadingEnv(false); }
  };
  const loadHandshake = async () => {
    try { setHandshake(await handshakeExport()); }
    catch (err) { console.warn("[BioDigital] handshake export failed:", err); }
  };
  const loadReceipts = async () => {
    try {
      const res = await listReceipts(20);
      setReceipts(res.receipts || []);
      setLifetimeDocks(res.lifetime_dock_count || 0);
    } catch (err) { console.warn("[BioDigital] receipts load failed:", err); }
  };

  useEffect(() => {
    loadEnv(); loadHandshake(); loadReceipts();
    // These loaders are stable in-module fns; intentionally one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitTelemetry = async (e) => {
    e?.preventDefault?.();
    setPosting(true);
    try {
      const payload = {
        temperature_c: temp ? Number(temp) : null,
        humidity_pct: hum ? Number(hum) : null,
        pressure_hpa: pres ? Number(pres) : null,
        data_velocity: vel ? Number(vel) : null,
        source: src || "manual",
      };
      const res = await ingestTelemetry(payload);
      toast.success("Telemetry docked", {
        description: res.decision.boosted
          ? `Boost: ${res.decision.reason}`
          : "Within comfort band",
      });
      setTemp(""); setHum(""); setPres(""); setVel("");
      await loadEnv();
    } catch (e) {
      toast.error("Ingest failed");
    } finally {
      setPosting(false);
    }
  };

  const broadcast = async () => {
    if (!targetUrl.trim()) { toast.error("Target URL required"); return; }
    setBroadcasting(true);
    try {
      const res = await handshakeBroadcast({
        target_url: targetUrl,
        target_name: targetName,
        bearer_token: bearer || null,
      });
      setBroadcastResult(res);
      toast.success(`Sent to ${res.target}`, {
        description: `HTTP ${res.status_code ?? "no-response"}`,
      });
    } catch (e) {
      toast.error("Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  const copyHandshake = async () => {
    if (!handshake) return;
    await navigator.clipboard.writeText(JSON.stringify(handshake, null, 2));
    toast.success("Handshake copied");
  };

  const envBadge = env?.decision?.boosted ? (
    <span className="inline-flex items-center gap-1.5 border border-amber-500/50 text-amber-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
      Boosted · strict
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 border border-emerald-500/50 text-emerald-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
      In band
    </span>
  );

  const sensitivity = env?.decision?.obsidian_sink_sensitivity;
  const sensitivityLabel = sensitivity === 1.0 ? "1.0 · STRICT" : sensitivity === 0.5 ? "0.5 · NORMAL" : "—";

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-12">
      <div>
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 flex items-center gap-2">
          <Activity className="h-3 w-3" /> Bio-Digital Bridge
        </div>
        <h1 className="font-heading text-4xl mt-1">Sentinel Body</h1>
        <div className="text-xs text-zinc-500 mt-2 max-w-2xl">
          Environment telemetry · multi-model handshake · docking receipts.
          The logic of the Sentinel lives in the layers; this page is where it
          touches the world.
        </div>
      </div>

      {/* Environment */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl flex items-center gap-3">
            <Thermometer className="h-5 w-5 text-amber-400" />
            Environment Layer
          </h2>
          <button
            onClick={loadEnv}
            data-testid="env-refresh-btn"
            className="p-2 border border-zinc-800 text-zinc-400 hover:border-emerald-500"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loadingEnv ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { label: "Temp", value: fmt(env?.latest?.temperature_c, "°C"), icon: Thermometer },
            { label: "Humidity", value: fmt(env?.latest?.humidity_pct, "%"), icon: Droplets },
            { label: "Pressure", value: fmt(env?.latest?.pressure_hpa, " hPa"), icon: Gauge },
            { label: "Velocity", value: fmt(env?.latest?.data_velocity, "/min"), icon: Activity },
            { label: "Source", value: env?.latest?.source || "—", icon: Radio },
          ].map((m) => (
            <div key={m.label} className="border border-zinc-800 bg-[#0b0b0d] p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                <m.icon className="h-3 w-3" /> {m.label}
              </div>
              <div className="mt-2 font-heading text-xl">{m.value}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {envBadge}
          <span
            data-testid="obsidian-sink-sensitivity"
            className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[9px] uppercase tracking-[0.25em] ${
              sensitivity === 1.0
                ? "border-rose-500/60 text-rose-400"
                : "border-zinc-700 text-zinc-400"
            }`}
          >
            Obsidian Sink · {sensitivityLabel}
          </span>
          <span className="text-[11px] text-zinc-500">
            {env?.decision?.reason ||
              "Sensitivity idle at 0.5 — pipeline running at normal filtration."}
          </span>
        </div>

        <form
          onSubmit={submitTelemetry}
          data-testid="telemetry-form"
          className="border border-zinc-800 bg-[#0b0b0d] p-6 grid grid-cols-2 md:grid-cols-5 gap-3"
        >
          {[
            { v: temp, set: setTemp, ph: "°C", test: "t-temp" },
            { v: hum, set: setHum, ph: "%", test: "t-hum" },
            { v: pres, set: setPres, ph: "hPa", test: "t-pres" },
            { v: vel, set: setVel, ph: "evt/min", test: "t-vel" },
          ].map((f) => (
            <input
              key={f.test}
              data-testid={f.test}
              value={f.v}
              onChange={(e) => f.set(e.target.value)}
              placeholder={f.ph}
              className="bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
          ))}
          <div className="flex gap-2 col-span-2 md:col-span-1">
            <input
              data-testid="t-src"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              placeholder="source"
              className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <button
              type="submit"
              disabled={posting}
              data-testid="submit-telemetry"
              className="shrink-0 px-4 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
            >
              {posting ? "…" : "Post"}
            </button>
          </div>
        </form>

        <div className="text-[10px] text-zinc-600 mt-3">
          BME280 users: POST readings to{" "}
          <code className="text-emerald-400">/api/environment/telemetry</code>{" "}
          from your Pythonista script or Raspberry Pi.
        </div>
      </section>

      {/* Handshake */}
      <section>
        <h2 className="font-heading text-2xl mb-4 flex items-center gap-3">
          <Send className="h-5 w-5 text-sky-400" />
          Multi-Model Handshake
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-zinc-800 bg-[#0b0b0d] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Exported protocol (read-only)
            </div>
            <pre
              data-testid="handshake-export"
              className="mt-3 text-[10px] text-zinc-300 whitespace-pre-wrap max-h-72 overflow-y-auto font-body"
            >
              {handshake ? JSON.stringify(handshake, null, 2) : "Loading…"}
            </pre>
            <button
              onClick={copyHandshake}
              data-testid="copy-handshake"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-400 text-[10px] uppercase tracking-[0.3em]"
            >
              <Copy className="h-3 w-3" /> Copy JSON
            </button>
          </div>

          <div className="border border-zinc-800 bg-[#0b0b0d] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Broadcast to peer AI
            </div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Target URL
            </label>
            <input
              data-testid="target-url-input"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              placeholder="https://peer-ai.example.com/handshake"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-4 block">
              Target name (optional)
            </label>
            <input
              data-testid="target-name-input"
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-4 block">
              Bearer token (optional)
            </label>
            <input
              data-testid="bearer-input"
              value={bearer}
              onChange={(e) => setBearer(e.target.value)}
              type="password"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
            <button
              onClick={broadcast}
              disabled={broadcasting || !isArchitect}
              title={isArchitect ? "Send handshake to peer AI" : "Architect only"}
              data-testid="broadcast-btn"
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-400 disabled:cursor-not-allowed text-xs uppercase tracking-[0.3em]"
            >
              {!isArchitect ? (
                <Lock className="h-4 w-4" />
              ) : broadcasting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {!isArchitect
                ? "Architect Only"
                : broadcasting
                ? "Sending…"
                : "Broadcast Handshake"}
            </button>
            {broadcastResult && (
              <div
                className="mt-5 border border-zinc-800 bg-[#0a0a0c] p-3 text-[11px]"
                data-testid="broadcast-result"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Response · HTTP {broadcastResult.status_code ?? "—"}
                </div>
                <pre className="mt-2 text-zinc-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {broadcastResult.response_excerpt?.slice(0, 1000) || "(empty)"}
                </pre>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Receipts */}
      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-heading text-2xl flex items-center gap-3">
            <FileDigit className="h-5 w-5 text-emerald-400" />
            Docking Receipts · HMAC ledger
          </h2>
          <div
            data-testid="lifetime-dock-count"
            className="border border-emerald-500/40 bg-[#0a1511] px-4 py-2 flex items-center gap-3"
          >
            <span className="text-[9px] uppercase tracking-[0.3em] text-emerald-500/70">
              Lifetime Docks
            </span>
            <span className="font-heading text-2xl text-emerald-400 tabular-nums">
              {lifetimeDocks}
            </span>
          </div>
        </div>

        {receipts.length === 0 ? (
          <div
            className="border border-zinc-800 bg-[#0b0b0d] p-10 text-center text-xs uppercase tracking-[0.3em] text-zinc-500"
            data-testid="receipts-empty"
          >
            No docking receipts yet. Every 2500-token pulse mints one.
          </div>
        ) : (
          <div className="border border-zinc-800" data-testid="receipts-table">
            {receipts.map((r, i) => (
              <div
                key={r.hash}
                className={`p-4 text-xs grid grid-cols-1 md:grid-cols-12 gap-3 ${
                  i !== receipts.length - 1 ? "border-b border-zinc-900" : ""
                }`}
              >
                <div className="md:col-span-1 text-[10px] uppercase tracking-[0.3em] text-emerald-400">
                  #{r.pulse_number}
                </div>
                <div className="md:col-span-3 text-zinc-500 text-[10px] uppercase tracking-[0.25em]">
                  {fmtTime(r.timestamp)}
                </div>
                <div className="md:col-span-4 text-zinc-300 font-body">
                  {r.session_tokens_total} tok · session {r.session_id?.slice(0, 8)}…
                  {r.lifetime_dock_count != null && (
                    <span className="ml-2 text-emerald-500/80">
                      · lifetime #{r.lifetime_dock_count}
                    </span>
                  )}
                </div>
                <div className="md:col-span-4 text-zinc-500 font-body break-all">
                  sig <span className="text-emerald-400">{r.signature_sha256?.slice(0, 16)}…</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
